import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { authService } from '@/api/services/authService';
import { isAuthenticated as hasAuthMarker, setAuthenticated, clearAuthenticated } from '@/api/apiClient';

const AuthContext = createContext(undefined);

// Backend redirects to FRONTEND_URL?oauth=success after a successful Google login,
// having already set the JWT cookies. Treat that as an incoming authenticated session.
const isOAuthRedirect = () =>
  new URLSearchParams(window.location.search).get('oauth') === 'success';

// One-shot, same-origin return target stashed before a Google redirect (e.g. "Sign in to
// reserve"). Google drops the returnUrl param, so we restore it here. 15-min TTL guards
// against a stale stash hijacking an unrelated later login.
const consumePostLoginRedirect = () => {
  try {
    const raw = localStorage.getItem('fg_post_login_redirect');
    if (!raw) return null;
    localStorage.removeItem('fg_post_login_redirect');
    const { u, t } = JSON.parse(raw);
    if (!u || !t || Date.now() - t > 15 * 60 * 1000) return null;
    const url = new URL(u, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.pathname + url.search + url.hash;
  } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(() => hasAuthMarker() || isOAuthRedirect());
  const [authResolved, setAuthResolved] = useState(() => !(hasAuthMarker() || isOAuthRedirect()));
  const [authError, setAuthError] = useState(null);

  const loadUser = useCallback(async () => {
    // OAuth landed us here with fresh JWT cookies but no client-side marker. Set the
    // marker, strip the ?oauth=success param, and hard-reload the clean URL once so the
    // app re-initialises with the cookies fully in place. Without the reload the first
    // paint can render logged-out until a manual refresh. No loop: the cleaned URL has
    // no ?oauth=success, so this branch won't run again.
    if (isOAuthRedirect()) {
      setAuthenticated();
      const params = new URLSearchParams(window.location.search);
      params.delete('oauth');
      const query = params.toString();
      const cleanUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
      // Prefer a same-origin return target stashed before the Google redirect (else clean Home URL).
      const target = consumePostLoginRedirect() || cleanUrl;
      window.location.replace(target);
      return;
    }

    if (!hasAuthMarker()) {
      setUser(null);
      setAuthError(null);
      setIsLoadingAuth(false);
      setAuthResolved(true);
      return;
    }

    try {
      setIsLoadingAuth(true);
      setAuthError(null);

      const currentUser = await authService.me();
      setUser(currentUser);
    } catch (error) {
      const status = error?.status ?? error?.response?.status;

      setUser(null);

      if (status === 401 || status === 403) {
        clearAuthenticated();
        setAuthError({
          type: 'auth_required',
          message: 'Session expired. Please log in again.',
        });
      } else {
        setAuthError({
          type: 'unknown',
          message: 'Failed to load user.',
        });
      }
    } finally {
      setIsLoadingAuth(false);
      setAuthResolved(true);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback(async (email, password) => {
    const data = await authService.login(email, password);
    setUser(data.user);
    setAuthError(null);
    setAuthResolved(true);
    return data;
  }, []);

  const register = useCallback(async (userData) => {
    const data = await authService.register(userData);
    setUser(data.user);
    setAuthError(null);
    setAuthResolved(true);
    return data;
  }, []);

  const logout = useCallback(async () => {
    setUser(null);
    setAuthError(null);
    clearAuthenticated();

    try {
      await authService.logout();
    } catch (error) {
      if (import.meta.env.DEV) console.error('Logout failed', error);
    }
  }, []);

  const deleteAccount = useCallback(async (password) => {
    await authService.deleteAccount(password);
    setUser(null);
    setAuthError(null);
    clearAuthenticated();
  }, []);

  const value = useMemo(() => ({
    user,
    setUser,
    isAuthenticated: !!user,
    isLoadingAuth,
    isLoadingPublicSettings: isLoadingAuth,
    authResolved,
    authError,
    login,
    register,
    logout,
    deleteAccount,
    reload: loadUser,
  }), [user, isLoadingAuth, authResolved, authError, login, register, logout, deleteAccount, loadUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
